import { isArray, isFunction, isObject, isUndefined, map } from 'lodash-es';

import type { JSX } from 'react';

type StringOptions = string[];
type OptionsMap = Record<string, string>;

export type Options = Option[];
export type ProvidedOptions = StringOptions | Options | OptionsMap;
export type OptionsProvider = ((filter: string) => ProvidedOptions | Promise<ProvidedOptions>);
export type OptionsOrProvider = ProvidedOptions | OptionsProvider;

export type EmptyOption = {
  value: undefined;
  label: string;
};

export type Option<T extends string = string> = {
  value: T;
  label: string;
};

export type EmptyOptionValue = EmptyOption['value'];
export type OptionValue = Option['value'];

export type OptionDecorator = (option: Option | EmptyOption) => JSX.Element;
// eslint-disable-next-line react/jsx-no-useless-fragment
export const DefaultOptionDecorator: OptionDecorator = option => <>{option.label}</>;

export const EMPTY_OPTION: EmptyOption = { value: undefined, label: '' };

export function toOptions(valuesOrOptions: ProvidedOptions) {
  return isOptionsMap(valuesOrOptions) ? optionsFromMap(valuesOrOptions) : optionsFromArray(valuesOrOptions);
}

const optionsFromArray = (valuesOrOptions: StringOptions | Options) => valuesOrOptions.map(valueOrOption => isObject(valueOrOption) ? valueOrOption : {
  value: valueOrOption,
  label: valueOrOption,
});

const optionsFromMap = (valuesOrOptions: OptionsMap) => map(
  valuesOrOptions,
  (value, key) => ({
    value: key,
    label: value,
  }),
);

export const isOption = (option: EmptyOption | Option | undefined): option is Option => !isUndefined(option?.value);
export const isEmptyOption = (option: EmptyOption | Option): option is EmptyOption => isUndefined(option.value);

export const isOptionsProvider = (optionsOrProvider: OptionsOrProvider): optionsOrProvider is OptionsProvider => isFunction(optionsOrProvider);

const isOptionsMap = (options: ProvidedOptions): options is OptionsMap => !isArray(options) && isObject(options);
